import { logger } from '@ecosystem/newron-client-lib';
import { contextClient } from '../client';
import {
  Context,
  ContextSettings,
  CustomFeature,
  CustomFeatureContext,
  Entity,
  Filters,
  FiltersValues,
  Params,
  SettingDefinition,
  SettingsValues,
} from '../types';
import { store, retrieve } from './cache';
import { getParamsForContext } from './utils';
import { deepEqual } from '../tools/object';
import { ContextSetting } from './types';
import { getMarketContext } from '../client/contextClient/contextQuery';

type SettingsSubscriber = (context: Context) => void;
type LoadingSubscriber = (flag: boolean) => void;
type EditSubscriber = (setting: SettingDefinition) => void;
type ErrorSubscriber = (message: string) => void;

/**
 *  Manages the various contexts used by propositions on the platform
 */
export class ContextManager {
  private static _instance: ContextManager;
  private settingsSubscribers: SettingsSubscriber[] = [];
  private loadingSubscribers: LoadingSubscriber[] = [];
  private editSubscribers: EditSubscriber[] = [];
  private errorSubscribers: ErrorSubscriber[] = [];
  private contexts: Record<string, Context> = {};
  private contextUrls: Record<string, string> = {};
  private active: string;
  private excludeFiltersForActive: boolean;
  private excludePriceFilterForActive: boolean;
  private activeCustomFeatureMenu: string | null = null;

  private constructor() {}

  /**
   * Cleans custom feature data by removing undefined keys and empty objects
   */
  private cleanCustomFeatureData(customFeature: CustomFeature): CustomFeature {
    if (!customFeature) return customFeature;

    const cleanedCustomFeature = {};

    for (const [pageName, pageData] of Object.entries(customFeature)) {
      if (!pageName || pageName === 'undefined') continue;

      const cleanedPageData = this.cleanPageData(pageData);
      if (Object.keys(cleanedPageData).length > 0) {
        cleanedCustomFeature[pageName] = cleanedPageData;
      }
    }

    return cleanedCustomFeature;
  }

  /**
   * Cleans page data by removing undefined origin menus
   */
  private cleanPageData(pageData: any): any {
    const cleanedPageData = {};

    for (const [originMenu, menuData] of Object.entries(pageData)) {
      if (!originMenu || originMenu === 'undefined') continue;
      cleanedPageData[originMenu] = menuData;
    }

    return cleanedPageData;
  }

  /**
   * Registers a new context with the manager and makes it the active context
   * @param name identifier for the context
   * @param url url to fetch context details from
   */
  async registerContext(
    name: string,
    url: string,
    excludeFilters?: boolean,
    excludePriceFilter?: boolean,
    collectionsContext?: {
      settings: SettingsValues;
      filters?: FiltersValues;
      customFeature?: CustomFeature;
    },
    isLiveInsight?: boolean
  ): Promise<void> {
    this.setActive(name, excludeFilters, excludePriceFilter);
    this.contextUrls[name] = url;

    const cached = collectionsContext || (await retrieve(name));
    if (cached) {
      const { settings, filters, customFeature } = cached;

      this.contexts[name] = {
        settings: {
          definitions: [],
          values: settings,
        },
        filters: !filters
          ? undefined
          : {
              sections: [],
              values: filters,
            },
        customFeature: this.cleanCustomFeatureData(customFeature),
      };
    }

    const isCollection = !!collectionsContext;
    this.fetchContext(isLiveInsight, isCollection);
  }

  /**
   * Gets a context by its name
   * @param name the named Context if found
   * @returns a Context object or undefined if not found
   */
  getByName(name: string): Context | undefined {
    return this.contexts[name];
  }

  /**
   * Gets context by name from current active context
   * @param name the named Context if found
   * @returns a Context object or undefined if not found
   */
  getByNameFromActiveContext(name: keyof Context): ContextSettings | Filters | CustomFeatureContext {
    return this.contexts[this.active][name];
  }

  /**
   * Gets the currently active context
   * @returns the active Context or undefined if there isn't one
   */
  get(): Context | undefined {
    const context = this.contexts[this.active];
    if (!context) {
      return;
    }
    const copyContext = { ...context };
    if (this.excludePriceFilterForActive) {
      this.removePriceFilterFromContext(copyContext);
      return {
        settings: copyContext.settings,
        filters: this.excludeFiltersForActive ? undefined : copyContext.filters,
        customFeature: copyContext.customFeature,
      };
    }

    return {
      settings: context.settings,
      filters: this.excludeFiltersForActive ? undefined : context.filters,
      customFeature: copyContext.customFeature,
    };
  }

  /**
   * Sets the active context
   * @param name identifier of the context to set as the active context
   */
  setActive(name: string, excludeFilters: boolean = false, excludePriceFilter: boolean = false) {
    this.active = name;
    this.excludeFiltersForActive = excludeFilters;
    this.excludePriceFilterForActive = excludePriceFilter;
    this.notifySettings();
  }

  setSettingAsRequiresEdit(name: string) {
    const context = this.get();
    if (!context) {
      return;
    }

    const {
      settings: { definitions },
    } = context;
    const toEdit = definitions.find(def => def.name === name);

    if (toEdit) {
      this.editSubscribers.forEach(sub => sub(toEdit));
    }
  }

  /**
   * Returns the definitions and values for settings on the active context. This function
   * will call the service to get options for any named setting that needs it
   * @param names names of the settings to get
   * @returns
   */
  async getSettings(names: string[]): Promise<Record<string, ContextSetting>> {
    const context = this.get();
    if (!context) {
      throw new Error('no active context');
    }

    const {
      settings: { definitions, values },
    } = context;

    const defsToGet: SettingDefinition[] = names
      .map(name => definitions.find(def => def.name === name))
      .filter(def => def !== undefined);

    if (defsToGet.some(def => !def.selection.options && def.selection.optionsUrl)) {
      const params = this.buildParams();
      const requests: Promise<(Entity | string)[]>[] = [];
      const defNames: string[] = [];
      defsToGet.forEach(def => {
        const {
          name,
          selection: { options, optionsUrl },
        } = def;
        if (optionsUrl && !options) {
          requests.push(getMarketContext(`${this.contextUrls[this.active]}/${optionsUrl}`, params));
          defNames.push(name);
        }
      });
      const results = await Promise.all(requests);
      results.forEach((options, idx) => {
        const defName = defNames[idx];
        const def = definitions.find(def => def.name === defName);
        def.selection.options = options;
      });
    }

    const settings: Record<string, ContextSetting> = {};
    defsToGet.forEach(def => {
      settings[def.name] = {
        definition: {
          ...{},
          ...def,
        },
        values: values[def.name] || [],
      };
    });

    return settings;
  }

  /**
   * Subscribe to changes in settings/filters definitions and values for the active context
   * @param subscriber callback
   */
  subscribeToSettingsChanges(subscriber: SettingsSubscriber) {
    this.settingsSubscribers.push(subscriber);
    const context = this.get();
    if (context) {
      subscriber({
        ...{},
        ...context,
      });
    }
  }

  /**
   *
   * @param subscriber callback you subscribed with
   */
  unsubscribeFromSettingsChanges(subscriber: SettingsSubscriber) {
    this.settingsSubscribers = this.settingsSubscribers.filter(s => s !== subscriber);
  }

  subscribeToLoading(subscriber: LoadingSubscriber) {
    this.loadingSubscribers.push(subscriber);
  }

  unsubscribeFromLoading(subscriber: LoadingSubscriber) {
    this.loadingSubscribers = this.loadingSubscribers.filter(s => s !== subscriber);
  }

  subscribeToEditRequired(subscriber: EditSubscriber) {
    this.editSubscribers.push(subscriber);
  }

  unsubscribeFromEditRequired(subscriber: EditSubscriber) {
    this.editSubscribers = this.editSubscribers.filter(s => s !== subscriber);
  }

  subscribeToError(subscriber: ErrorSubscriber) {
    this.errorSubscribers.push(subscriber);
  }

  unsubscribeFromError(subscriber: ErrorSubscriber) {
    this.errorSubscribers = this.errorSubscribers.filter(s => s !== subscriber);
  }

  /**
   * Updates values for settings the active context. Once the updates are applied, the url
   * for the updated context will be called to refetch the context
   * @param updates updated settings values
   * @param isLiveView if true, the context will be fetched with isLive=true
   * @param skipRefetch if true, the context will not be refetched after the updates are applied and the settings will be updated in the store
   * @returns
   */
  updateSettingsValues(updates: Record<string, Entity[]>, isLiveView?: boolean, skipRefetch?: boolean) {
    const context = this.get();
    if (!context) {
      logger.warning('no active context');
      return;
    }

    const newValues = {
      ...{},
      ...context.settings.values,
      ...updates,
    };
    if (!isLiveView && deepEqual(context.settings.values, newValues)) {
      return;
    }
    context.settings.values = newValues;

    if (!skipRefetch) {
      this.fetchContext(isLiveView);
    } else {
      this.contexts[this.active] = context;
      store(this.active, context.settings.values, context.filters?.values, context.customFeature);
    }
  }

  updateCustomFeatureValues(updates: CustomFeatureContext) {
    let context = this.get();
    if (!context) {
      logger.warning('no active context');
      return;
    }

    const updatedCustomFeature = {
      ...context.customFeature,
    };

    Object.keys(updates.customFeature).forEach(pageName => {
      if (!updatedCustomFeature[pageName]) {
        updatedCustomFeature[pageName] = {};
      }

      Object.keys(updates.customFeature[pageName]).forEach(originMenu => {
        // Skip undefined keys
        if (originMenu === 'undefined' || originMenu === undefined) {
          return;
        }

        updatedCustomFeature[pageName][originMenu] = {
          ...updatedCustomFeature[pageName][originMenu],
          ...updates.customFeature[pageName][originMenu],
        };
      });
    });

    const newValues = {
      ...context,
      customFeature: updatedCustomFeature,
    };
    context = newValues;
    this.contexts[this.active] = context;
    store(this.active, context.settings.values, context.filters?.values, context.customFeature);
    this.notifySettings();
  }

  /**
   * Updates values for filters within the active context
   * @param updates updated filters values
   * @returns
   */
  updateFilterValues(updates: FiltersValues) {
    const context = this.get();
    if (!context) {
      logger.warning('no active context');
      return;
    }
    const newValues: FiltersValues = {
      ...{},
      ...context.filters.values,
      ...updates,
    };
    const toRemove: string[] = [];
    Object.keys(newValues).forEach(key => {
      if (newValues[key].length === 0) {
        toRemove.push(key);
      }
    });
    toRemove.forEach(key => {
      delete newValues[key];
    });
    context.filters.values = newValues;
    store(this.active, context.settings.values, context.filters?.values);
    this.notifySettings();
  }

  clearFilterValues() {
    const context = this.get();
    if (!context) {
      logger.warning('no active context');
      return;
    }
    context.filters.values = {};
    store(this.active, context.settings.values, context.filters?.values);
    this.notifySettings();
  }

  set(newContext: Context) {
    const context = this.get();
    if (!context) {
      return;
    }

    this.contexts[this.active] = { ...newContext };
    store(this.active, newContext.settings.values, newContext.filters?.values);
    this.notifySettings();
  }

  /**
   * Clears the options for settings in the active context
   * @param names names of the settings to clear options for
   */
  clearSettingOptions(names: string[]): void {
    const context = this.get();
    if (!context) {
      return;
    }

    names.forEach(name => {
      const def = context.settings.definitions.find(d => d.name === name);
      if (def?.selection) {
        def.selection.options = null;
      }
    });

    store(this.active, context.settings.values, context.filters?.values, context.customFeature);
  }

  /**
   * Notifies all subscribes of the settings in the active context
   */
  private notifySettings() {
    const context = this.get();
    if (!context) {
      return;
    }
    this.settingsSubscribers.forEach(sub =>
      sub({
        ...{},
        ...context,
      })
    );
  }

  private notifyLoading(isLoading: boolean) {
    this.loadingSubscribers.forEach(sub => sub(isLoading));
  }

  private notifyError(error: string) {
    this.errorSubscribers.forEach(sub => sub(error));
  }
  /**
   * Builds and returns a params object containing the values for settings and filters
   * within a context
   * @param name identifier for the context to build from
   * @returns a Params object
   */
  private buildParams(): Params {
    const params: Params = {};
    const context = this.get();
    if (!context) {
      return params;
    }
    const { settings, filters } = context;
    Object.keys(settings.values).forEach(field => {
      params[field] = settings.values[field].map(e => e.value);
    });
    if (filters) {
      Object.keys(filters.values).forEach(field => {
        params[field] = filters.values[field].map(e => e.value);
      });
    }
    return params;
  }

  private removePriceFilterFromContext(context: Context) {
    context.filters.sections.forEach(section => {
      if (section.ungrouped?.definitions) {
        section.ungrouped.definitions = section.ungrouped.definitions.filter(def => def.name !== 'price');
      }
    });
    if (context.filters.values['price']) {
      delete context.filters.values['price'];
    }
  }

  /**
   * Fetches a new instance of the active context, stores it and notifies any
   * subscribers to it
   */
  private fetchContext(isLiveInsight: boolean = false, isCollection: boolean = false) {
    let params: Params = {};
    const localContext = this.contexts[this.active];
    if (localContext) {
      params = getParamsForContext(localContext);
    }
    this.notifyLoading(true);
    contextClient
      .getContext(this.contextUrls[this.active], {
        ...params,
        isLive: isLiveInsight ? [String(true)] : undefined,
      })
      .then(context => {
        // if it's not a collection, store the context
        if (!isCollection) {
          this.contexts[this.active] = { customFeature: localContext?.customFeature, ...context };
          store(this.active, context.settings.values, context.filters?.values, localContext?.customFeature);
        }
        this.notifySettings();
      })
      .catch(err => {
        console.error('CAUGHT ERROR ', err);
        this.notifyError(err.message);
      })
      .finally(() => {
        this.notifyLoading(false);
      });
  }

  getActiveCustomFeatureMenu(): string | null {
    return this.activeCustomFeatureMenu;
  }

  setActiveCustomFeatureMenu(menu: string | null): void {
    this.activeCustomFeatureMenu = menu;
    this.notifySettings();
  }

  shouldDisableCustomOptions(currentParamName: string): boolean {
    return this.activeCustomFeatureMenu !== null && this.activeCustomFeatureMenu !== currentParamName;
  }

  /**
   * instance getter for the singleton ContextManager
   * @returns the single instance of ContextManager
   */
  static getInstance(): ContextManager {
    if (!ContextManager._instance) {
      ContextManager._instance = new ContextManager();
    }
    return ContextManager._instance;
  }
}
